import type { FieldValues, Path, UseFormReturn } from 'react-hook-form'

import { ApiError } from '@/lib/api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

export type PhoneConflict = { id: string; name: string }

// Bảng ánh xạ (field, rule) → getter chuỗi VI. Chỉ cover các tag hiện có ở
// CreateCustomerRequest bên BE (name: required/min/max; email: email). Khi BE
// thêm rule mới, thêm entry ở đây; fallback là chuỗi EN raw để không mất info.
const FIELD_RULE_VI: Record<string, () => string> = {
    'name:required': () => m.customer_error_name_required(),
    'name:min': () => m.customer_error_name_min(),
    'name:max': () => m.customer_error_name_max(),
    'email:email': () => m.customer_error_email_invalid(),
}

// BE formatValidationErrors trả string dạng "<field> is required", "must be at
// least N characters", "must be at most N characters", "is invalid". Đây là
// nguồn duy nhất để suy ra rule — nếu BE đổi format, cập nhật hàm này.
function inferRule(msg: string): string {
    if (msg.includes('is required')) return 'required'
    if (msg.includes('at least')) return 'min'
    if (msg.includes('at most')) return 'max'
    if (msg.includes('is invalid')) return 'email'
    return 'unknown'
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v)
}

export function applyCustomerCreateError<T extends FieldValues>(
    err: unknown,
    form: UseFormReturn<T>,
): { toast: string | null; phoneConflict: PhoneConflict | null } {
    if (!(err instanceof ApiError)) {
        return { toast: m.customer_error_generic(), phoneConflict: null }
    }

    if (err.code === 'VALIDATION_ERROR' && isPlainObject(err.details)) {
        for (const [field, msgEn] of Object.entries(err.details)) {
            if (typeof msgEn !== 'string') continue
            const rule = inferRule(msgEn)
            const viGetter = FIELD_RULE_VI[`${field}:${rule}`]
            const viMsg = viGetter ? viGetter() : msgEn
            form.setError(field as Path<T>, { message: viMsg })
        }
        return { toast: null, phoneConflict: null }
    }

    // Luật tên khách N5: lỗi tên hiện cạnh ô tên, không toast.
    if (err.code === 'CUSTOMER_NAME_INVALID') {
        form.setError('name' as Path<T>, {
            message:
                resolveApiErrorMessage(err.code, err.params) ??
                m.validation_required_name(),
        })
        return { toast: null, phoneConflict: null }
    }

    if (err.code === 'PHONE_EXISTS' && isPlainObject(err.details)) {
        const d = err.details as Record<string, string>
        form.setError('phone' as Path<T>, { message: '' })
        return {
            toast: null,
            phoneConflict: {
                id: d.conflicting_customer_id,
                name: d.conflicting_customer_name,
            },
        }
    }

    return {
        toast: err.message || m.customer_error_generic(),
        phoneConflict: null,
    }
}
